import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../login/AuthProvider";
import { extend } from "../../components/apipath";
import Swal from "sweetalert2";

function ExtendRequestForm() {
    const navigate = useNavigate();
    const { token, mem_id, mem_status, authorized } = useAuth();

    // ===== 狀態 =====
    const [authReady, setAuthReady] = useState(false);
    const [myRequests, setMyRequests] = useState([]);
    const [loadingList, setLoadingList] = useState(true);
    const [listError, setListError] = useState("");

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        studentName: "",
        subject: [],
        proofFile: null,
        currentExpiry: "",
        memo: "",
    });

    // ===== 等待 Auth 準備好 =====
    useEffect(() => {
        if (token && mem_id != null) setAuthReady(true);
    }, [token, mem_id]);

    // ===== 授權檢查（等有值才判斷）=====
    useEffect(() => {
        if (authorized == null || mem_status == null) return;
        if (!authorized || String(mem_status) !== "1") {
        Swal.fire({
            icon: "warning",
            title: "學員專屬功能",
            text: "此頁面僅限學員使用，請先完成購買並登入",
            confirmButtonText: "了解",
            confirmButtonColor: "#224040",
        }).then(() => navigate("/member/memberlogin"));
        }
    }, [authorized, mem_status, navigate]);

    // ===== 帶入學員姓名（localStorage 的 auth）=====
    useEffect(() => {
        const authData = JSON.parse(localStorage.getItem("auth"));
        if (authData?.chinese_name) {
        setFormData((prev) => ({ ...prev, studentName: authData.chinese_name }));
        }
    }, []);

    // ===== 抓「我的展延紀錄」===== 
    useEffect(() => {
        if (!authReady) return;

        const fetchMyStatus = async () => {
        try {
            setLoadingList(true);
            setListError("");
            const url = `${ensureSlash(extend)}my-extend/${mem_id}`;
            const res = await axios.get(url, { headers: { token } });

            // 彈性解析
            const ok = res.data?.status === 200 || res.status === 200;
            const list =
            res.data?.data?.request_history ??
            res.data?.request_history ??
            res.data?.data ??
            [];

            setMyRequests(ok && Array.isArray(list) ? list : []);
            if (!ok) {
            setListError("查詢失敗，請稍後再試。");
            console.warn("my-extend 非 200：", res.data);
            }
        } catch (err) {
            console.error("申請查詢失敗", err);
            setListError("查詢發生錯誤，請稍後再試。");
            setMyRequests([]);
        } finally {
            setLoadingList(false);
        }
        };

        fetchMyStatus();
    }, [authReady, mem_id, token]);

    // ===== 處理 input 變更 =====
    const handleChange = (e) => {
        const { name, value, type, files } = e.target;
        setFormData((prev) => ({
        ...prev,
        [name]: type === "file" ? files?.[0] ?? null : value,
        }));
    };

    // ===== 科目多選 =====
    const handleCheckboxChange = (e) => {
        const { value, checked } = e.target;
        setFormData((prev) => {
        const next = checked
            ? [...prev.subject, value]
            : prev.subject.filter((s) => s !== value);
        return { ...prev, subject: next };
        });
    };

    // ===== 上傳檔案 =====
    const handleFileUpload = async (file) => {
        if (!file) throw new Error("請選擇要上傳的檔案");
        const fd = new FormData();
        fd.append("file", file);

        const url = `${ensureSlash(extend)}upload`;
        const resp = await axios.post(url, fd, {
        headers: {
            "Content-Type": "multipart/form-data",
            token, // 若後端驗證需要
        },
        });

        if (resp.status === 200 && resp.data?.filePath) {
        return resp.data.filePath;
        }
        throw new Error(resp.data?.message || "檔案上傳失敗");
    };

    // ===== 計算 requested_expiry（currentExpiry + 6 個月）=====
    // const computedRequestedExpiry = useMemo(() => {
    //     if (!formData.currentExpiry) return "";
    //     const d = new Date(formData.currentExpiry);
    //     if (Number.isNaN(d.getTime())) return "";
    //     const plus6 = new Date(d.setMonth(d.getMonth() + 6));
    //     return plus6.toISOString().split("T")[0];
    // }, [formData.currentExpiry]);

    // ===== 送出表單 =====
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;

        // 前端基本驗證
        if (!formData.studentName?.trim()) {
        return toastError("請確認學員姓名");
        }
        if (!formData.currentExpiry) {
        return toastError("請選擇目前軟體到期日");
        }
        if (!formData.proofFile) {
        return toastError("請上傳證明文件");
        }
        if (!formData.subject?.length) {
        return toastError("請至少勾選一個科目");
        }

        setIsSubmitting(true);
        try {
        const uploadedFilePath = await handleFileUpload(formData.proofFile);

        const payload = {
            mem_id,
            student_name: formData.studentName,
            subject: formData.subject.join(","), // 後端若收陣列，改為陣列
            proof_file: uploadedFilePath,
            status: "審核中",
            current_expiry: formData.currentExpiry,
            // requested_expiry: computedRequestedExpiry || formData.currentExpiry,
            requested_expiry: formData.currentExpiry,
            memo: formData.memo,
        };

        const url = ensureSlash(extend); // 假設 extend = ".../extend/" 或 ".../extend"
        const response = await axios.post(url, payload, { headers: { token } });

        if (response.status === 200) {
            await Swal.fire({
            icon: "success",
            title: "申請成功",
            text: "請等待管理員審核！",
            confirmButtonText: "確認",
            timer: 3000,
            timerProgressBar: true,
            });
            window.location.href = "/member";
            return;
        }

        throw new Error(response.data?.message || "提交失敗，請稍後再試。");
        } catch (error) {
        console.error("申請失敗:", error);
        const msg =
            error.response?.data?.message || error.message || "提交失敗，請稍後再試。";
        Swal.fire({
            icon: "error",
            title: "申請失敗",
            text: msg,
            confirmButtonText: "確定",
        });
        } finally {
        setIsSubmitting(false);
        }
    };
    const formatDate = (dateString) => {
        if (!dateString) return "—";
        return new Date(dateString).toISOString().slice(0, 10); // 永遠 YYYY-MM-DD
    };
    return (
        <div className="container mb100 mt100">
        <h2 className="font-weight-bold pb-5 text-center">會員軟體展延申請</h2>

        {/* 申請紀錄 */}
        <div className="container mb100 mt100">
            <h5 className="mt-4">申請紀錄</h5>
            <table className="table table-striped table-bordered mt-3">
            <thead className="thead-dark">
                <tr>
                <th>申請時間</th>
                <th>科目</th>
                <th>軟體到期日</th>
                <th>備註</th>
                <th>狀態</th>
                </tr>
            </thead>
            <tbody>
                {loadingList ? (
                <tr>
                    <td colSpan="5" className="text-center">
                    載入中…
                    </td>
                </tr>
                ) : listError ? (
                <tr>
                    <td colSpan="5" className="text-center text-danger">
                    {listError}
                    </td>
                </tr>
                ) : myRequests?.length > 0 ? (
                myRequests.map((r, i) => {
                    const createdAt = formatDate(r.timestamp);
                    const subject = r.subject || r.core_subject || "—";
                    const currentExpiry = formatDate(r.current_expiry);
                    const memo = r.memo || "—";
                    const status = r.status || "—";
                    return (
                        <tr key={i}>
                            <td>{createdAt}</td>
                            <td>{subject}</td>
                            <td>{currentExpiry}</td>
                            <td>{memo}</td>
                            <td>{status}</td>
                        </tr>
                    );
                })
                ) : (
                <tr>
                    <td colSpan="5" className="text-center">
                    尚無申請紀錄
                    </td>
                </tr>
                )}
            </tbody>
            </table>
        </div>

        {/* 申請表單 */}
        <div className="card w-75 p-5 m-auto">
            <div className="card-body">
            <form onSubmit={handleSubmit}>
                {/* 學員姓名 */}
                <div className="form-group mb-4">
                <label>學員姓名</label>
                <input
                    type="text"
                    className="form-control"
                    name="studentName"
                    value={formData.studentName}
                    readOnly
                />
                </div>

               {/* 欲申請展延的科目 */}
                <div className="form-group mb-4">
                <label>欲申請展延的科目</label>

                {/* CPA */}
                <div className="mb-3">
                    <h5>CPA</h5>

                    <div className="checkbox-group">
                    {["FAR", "REG", "AUD", "BAR", "TCP", "ISC"].map((s) => (
                        <div className="form-check" key={s}>
                        <input
                            className="form-check-input"
                            type="checkbox"
                            name="subject"
                            value={s}
                            checked={formData.subject.includes(s)}
                            onChange={handleCheckboxChange}
                        />
                        <label className="form-check-label">{s}</label>
                        </div>
                    ))}
                    </div>
                </div>

                {/* EA */}
                <div className="mb-3">
                    <h5>EA</h5>

                    <div className="checkbox-group">
                    {["EA Part1", "EA Part2", "EA Part3"].map((s) => (
                        <div className="form-check" key={s}>
                        <input
                            className="form-check-input"
                            type="checkbox"
                            name="subject"
                            value={s}
                            checked={formData.subject.includes(s)}
                            onChange={handleCheckboxChange}
                        />
                        <label className="form-check-label">{s}</label>
                        </div>
                    ))}
                    </div>
                </div>
                </div>

                {/* 證明文件上傳 */}
                <div className="form-group mb-4">
                <label>證明文件上傳</label>
                <div  className="mt-2">
                    <small>
                若您「即將應考」，請提供該科考場預約證明收據 (Prometric appointment confirmation 電子郵件)，將該封信以「列印 -- 存為 PDF 檔案」方式輸出後，提供給我們。

                若您「一個月內剛應考但未通過」，請提供該科成績單 (score notice，可於 CPA Portal 下載)， 提供給我們。

                請提供完整信件 PDF、或完整成績單 PDF 檔，其中包含您的完整考生資訊。部分截圖和資料不全者，Becker 將不予採用、可能造成處理時間的耽延，敬請依上述方式完整提供。
                </small>
                </div>
                <input
                    type="file"
                    className="form-control"
                    name="proofFile"
                    onChange={handleChange}
                    accept=".pdf,.jpg,.jpeg,.png"
                    required
                />
                </div>

                {/* 軟體到期日 */}
                <div className="form-group mb-4">
                <label>目前軟體到期日</label>
                <br />
                <small>您的軟體到期日顯示於 Becker 軟體右上角「License」頁面</small>
                <input
                    type="date"
                    className="form-control"
                    name="currentExpiry"
                    value={formData.currentExpiry}
                    onChange={handleChange}
                    required
                />
                </div>

                {/* 備註 */}
                <div className="form-group mb-4">
                <label>備註</label>
                <textarea
                    className="form-control"
                    name="memo"
                    rows="3"
                    value={formData.memo}
                    onChange={handleChange}
                />
                </div>

                {/* 隱藏：顯示將申請到期日（純顯示用，實際送出用 computedRequestedExpiry） */}
                {/* {formData.currentExpiry && (
                <div className="form-group mb-3">
                    <small className="text-muted">
                    系統將把「申請到期日」設定為：{computedRequestedExpiry || "—"}（目前到期日 + 6 個月）
                    </small>
                </div>
                )} */}

                {/* 提交按鈕 */}
                <button type="submit" className="btn btn-primary w-100" disabled={isSubmitting}>
                {isSubmitting ? "提交中..." : "提交申請"}
                </button>
            </form>
            </div>
        </div>
        </div>
    );
    }

    /** 工具：確保 base URL 有結尾斜線再拼接 */
    function ensureSlash(base) {
    if (!base) return "/";
    return base.endsWith("/") ? base : base + "/";
    }

    /** 工具：快速錯誤提示 */
    function toastError(msg) {
    Swal.fire({
        icon: "error",
        title: "欄位未填完整",
        text: msg,
        confirmButtonText: "確定",
    });
}

export default ExtendRequestForm;
